import _ from 'lodash'
import useIdToFindCategory from '@/views/creative/utils/use-id-to-find-category'
import JSONParse from '@/utils/JSONParse'
import { isJpg, isPng, isVideoResourceAndHasAudio } from '@com/utils/file'
import {
  gameConfigCategoryResourceType,
  i18nCategoryResourceType,
} from '@/store/creative/common/category'
import { fillFilePath } from '@/views/creative/utils'
import { isPresetPrefix } from '@/views/creative/utils/preset-utils'

/**
 * 是否是前端生成的资源 id
 * 前端上传的资源会生成一个临时 id，默认是 string 类型，接口的是 number 类型
 * @param id
 * @returns {boolean}
 */
export const isFeResourceId = (id) => {
  return _.isNumber(id)
}

// 资源类型，主要存储不同资源类型的配置信息，如前缀
export const resourceConfig = {
  frameSequence: {
    prefix: 'xlz_',
    fileType: 'zip',
  },
  dragonBones: {
    prefix: 'lg_',
    fileType: 'zip',
  },
  model: {
    prefix: 'model_',
    fileType: 'zip',
  },
  image: {
    fileType: 'image',
  },
  video: {
    fileType: 'video',
  },
  audio: {
    fileType: 'audio',
  },
}

export const defaultImageFilters = {
  hue: 0,
  saturation: 0,
  luminance: 0,
  blur: 0,
  red: 0,
  green: 0,
  blue: 0,
}

export const defaultEnableFillColor = false

/**
 * 使用分类的 resource_type 获取其基础类型
 * 'normal', 'zip', 'group', 'lang' 的基础类型是 resource
 * @param type {string}
 */
export const getBaseResourceType = (type) => {
  const resource = ['normal', 'zip', 'group', 'lang']
  if (resource.some((item) => item === type)) return 'resource'
  if (
    type === i18nCategoryResourceType ||
    type === gameConfigCategoryResourceType
  ) {
    return type
  }
}

/**
 * 初始化资源 extra 信息，不同的类型资源信息不一样
 * @param resource
 * @returns {{origin_file_url: *, config: any}}
 */
export const initResourceExtra = (resource) => {
  const extra = resource.extra || {}
  const isImage = isPng(resource.file_url) || isJpg(resource.file_url)
  const extraConfig = extra.config ? JSONParse(extra.config) : {}
  // 图片才需要 enableFillColor 与 filters 属性
  let newExtraConfig = {}
  if (isImage) {
    newExtraConfig = {
      enableFillColor: defaultEnableFillColor,
      filters: {
        ...defaultImageFilters,
      },
      ...extraConfig,
    }
  } else {
    newExtraConfig = {
      ...extraConfig,
    }
  }
  return {
    ...extra,
    // 如果 extra 有 origin_file_url，则使用该 url；没有则使用 file_url
    origin_file_url: extra.origin_file_url || resource.file_url,
    // 补默认配置
    config: newExtraConfig,
    hasExtra: JSON.stringify(extra) !== '{}', // 判断extra是否为空，为空的话模型资源不能够下载
  }
}

/**
 * 加工静态资源列表
 * @param list
 * @returns {*}
 */
export const cookResources = (list, cb) => {
  // 保留资源的 file_path 与 preview_urls 属性（文件在 playable 中的路径），用于实时预览替换资源
  // 为什么增加这两个属性，因为在前期开发时基础包资源不可修改，使用了 file_path 与 preview_urls 这两个查找资源在 playable 中的路径
  // 替换其他资源时，会更改 file_path 或 preview_urls 属性
  // 所以在后期开发时开放了基础包资源的修改功能，导致 file_path 或 preview_urls 被修改，修改后这个属性的值后就无法再找到 playable 对应的资源，导致实时预览失败
  _.each(list, (resourceItem, key) => {
    const basicResource = resourceItem.find((item) => item.is_basic === 1) || {}
    list[key] = resourceItem.map((item) => {
      _.isFunction(cb) && cb(item)
      // 补缺失的属性
      const resource = {
        ...item,
        extra: item.extra ? JSONParse(item.extra) : {},
      }
      const extra = initResourceExtra(resource)
      return {
        ...item,
        extra,
        file_path_for_live_preview: basicResource.file_path,
        real_file_path: fillFilePath(
          item.file_path,
          isPresetPrefix(item.prefix),
        ),
        preview_urls_for_live_preview: _.cloneDeep(basicResource.preview_urls),
      }
    })
  })
  return list
}

/**
 * 拍平资源列表
 * @param {{id: {}[]}} resourceList
 * @returns []
 */
const flattenResourceList = (resourceList) => {
  const cloneList = _.cloneDeep(resourceList)
  let list = []
  _.each(cloneList, (item) => {
    list = [...list, ...item]
  })
  return list
}

// zip 包，除了语言包，file_urls 都是没有意义的
// 因为只有语言才会更新 zip 包中的文件，其他 zip 包都是作为一整个整体更新
// TODO：除了语言包，都不需要传 file_urls
const resourceUtils = {
  create(resource, { isMultiCustom }) {
    const isImage = isPng(resource.file_url) || isJpg(resource.file_url)
    const isVideoAudio = isVideoResourceAndHasAudio(resource)
    // 如果是模型或者图片，则保留 extra 字段，因为模型下载依赖 extra 里面的 fbx
    const isKeepExtra = isImage || resource.isModel || isVideoAudio
    // 新增云端图片
    if (resource.resource_origin === 'cloud') {
      return _.pick(resource, [
        'res_id',
        'prefix',
        'resource_type',
        'file_name',
        'action',
        'is_default',
        'res_tag_ids',
        'file',
        'category_id',
        // 图片资源增加 extra 属性
        isKeepExtra ? 'extra' : '',
      ])
    }

    // 本地上传
    // resource.resource_origin === 'local'
    if (resource.file_path) resource.file = resource.file_path
    if (resource.preview_urls) resource.file_urls = resource.preview_urls

    if (resource.res_source === 'assets' || resource.res_source === 'common') {
      // 模型资源新增 extra 属性
      return _.pick(resource, [
        'res_id',
        'res_source',
        'action',
        'prefix',
        'resource_type',
        'file_name',
        'file',
        'is_default',
        'res_name',
        'res_tag_ids',
        'file_urls', // zip 包独有
        'zip_sign', // 语言包独有
        'category_id',
        'fe_id',
        // 图片资源增加 extra 属性
        isKeepExtra ? 'extra' : '',
      ])
    }

    // 本地上传资源库类型文件
    if (resource.isRepository) {
      // file_urls/zip_sign 是 zip 独有属性
      return _.pick(resource, [
        'action',
        'prefix',
        'resource_type',
        'file_name',
        'file',
        'is_default',
        'res_name',
        'res_tag_ids',
        'res_cat1_id',
        'res_cat2_id',
        'file_urls', // zip 包独有
        'category_id',
        'fe_id',
        // 图片资源增加 extra 属性
        isKeepExtra ? 'extra' : '',
      ])
    }

    // 本地上传非资源库类型文件
    return _.pick(resource, [
      'action',
      'prefix',
      'resource_type',
      'file_name',
      'file',
      'is_default',
      'res_name',
      'res_tag_ids',
      'file_urls', // zip 包独有
      'zip_sign', // 语言包独有
      'category_id',
      'fe_id',
      // 图片资源增加 extra 属性
      isKeepExtra ? 'extra' : '',
    ])
  },

  update(resource) {
    if (resource.file_path) resource.file = resource.file_path
    if (resource.preview_urls) resource.file_urls = resource.preview_urls

    const isImage = isPng(resource.file_url) || isJpg(resource.file_url)
    // 通过prefix前缀判断是否序列帧
    const isXlz = resource.prefix.indexOf('xlz_') > -1
    const isVideoAudio = isVideoResourceAndHasAudio(resource)
    // 如果是模型或者图片，则保留 extra 字段，因为模型下载依赖 extra 里面的 fbx
    const isKeepExtra = isImage || resource.isModel || isVideoAudio
    const paths = [
      // 通用
      'res_id', // 更新云端选择的图片才有这个属性
      'action',
      'prefix',
      'resource_type',
      'file_name',
      'file',
      'is_default',
      'file_urls', // zip 包独有
      'zip_sign', // 语言包独有
      // 资源库资源独有
      'res_tag_ids',
      // 图片资源增加 extra 属性
      isKeepExtra ? 'extra' : '',
      // 更新本地资源才有有这个属性
      isFeResourceId(resource.id) ? 'id' : '',
    ]

    if (resource.res_source === 'assets' || resource.res_source === 'common') {
      paths.push('res_source')
    }

    const newResource = _.pick(resource, paths)

    // 当存在 file_urls 和 file 的时候，删除 file ，因为单文件用 file ，多文件用 file_urls
    // 存在特例，xlz使用file更新
    if (
      newResource.file_urls &&
      newResource.file_urls.length &&
      newResource.file &&
      !isXlz
    ) {
      delete newResource.file
    }

    // 如果有新的文件，说明是更新图片资源，对于资源库资源来说，更新资源其实就是新增资源，所以将 res_id 去掉，避免后端逻辑处理出现错误
    if (newResource.file && newResource.res_id) {
      delete newResource.res_id
    }

    return newResource
  },

  del(resource) {
    // 删除临时的资源不需要告知服务端该资源被删除了
    if (isFeResourceId(resource.id)) {
      // 删除只需要 id 和 action
      return { ..._.pick(resource, ['id', 'action']) }
    }
    return null
  },
}

/**
 * 加工保存接口 resource 数据
 * @param newResourceList {[]}
 * @param isMultiCustom {boolean}
 * @returns {[]}
 */
export const cookResourceConfig = (newResourceList, { isMultiCustom }) => {
  const resourceFilter = (resource) => {
    // 没有 action 代表该资源不需要处理（或者是 reset 分类的所有资源）
    if (typeof resource.action === 'string') {
      // 没 id，代表包不是包原来的资源
      // 如从本地上传或云端上传的资源被删除了，所以在更新时不需要传
      if (resource.action === 'del') {
        return !!resource.id
      }
      return true
    }
    return false
  }

  let list = flattenResourceList(newResourceList)
    .filter(resourceFilter)
    .map((resource) => {
      let newResource = null
      if (resource.action === 'create') {
        newResource = resourceUtils.create(resource, {
          isMultiCustom,
        })
      }

      if (resource.action === 'del') {
        newResource = resourceUtils.del(resource)
      }

      if (resource.action === 'update') {
        newResource = resourceUtils.update(resource)
      }

      // 重置
      if (resource.action === 'reset') {
        newResource = resource
      }

      if (newResource && newResource.extra) {
        const extra = {}
        if (newResource.extra.origin_file_url) {
          extra.origin_file_url = newResource.extra.origin_file_url
        }
        if (newResource.extra.config) {
          extra.config = JSON.stringify(newResource.extra.config)
        }
        if (isVideoResourceAndHasAudio(newResource)) {
          extra.ext_audio_path = newResource.extra.ext_audio_path
        }
        newResource.extra = extra
      }

      // 因为 res_id 改成 bigInt 结构，所以需要转换成 string
      if (newResource.res_id) {
        newResource.res_id = String(newResource.res_id)
      }

      return newResource
    })
    .filter((resource) => resource !== null)
  // 因为关联资源会有一样的数据数据，所以做一次去重
  list = _.uniqWith(list, _.isEqual)
  const delList = list.filter((item) => item.action === 'del')
  const createAndUpdateList = list.filter((item) => item.action !== 'del')
  return [...createAndUpdateList, ...delList]
}

export function getDefaultResource(resource, category) {
  if (_.isEmpty(resource) || _.isEmpty(category)) return []

  // 存储当前预览的资源
  const currentLivePreviewResource = {}
  _.each(resource || {}, (item, key) => {
    const defaultResource = {
      ...item.find((j) => j.is_default === 1),
    }
    const categoryProperties = (
      useIdToFindCategory({
        category,
        categoryId: defaultResource.category_id,
      }) || {}
    ).properties
    currentLivePreviewResource[key] = {
      ...defaultResource,
      categoryProperties,
    }
  })
  return _.cloneDeep(currentLivePreviewResource)
}

/**
 * 根据是否开启填充颜色加工数据
 * @param filters
 * @param enableFillColor {boolean}
 */
export function getFilterConfigForFilter(filters, enableFillColor) {
  if (enableFillColor) {
    return filters
  }
  return {
    hue: filters.hue,
    saturation: filters.saturation,
    luminance: filters.luminance,
    blur: filters.blur,
  }
}
